import { BRAND_SOURCE_KEY, BRAND_SITE_HOST, BRAND_PERSON_NAME } from "@/lib/brandConfig";
/**
 * Página /exclusive-ar — Mismo layout y detalles que /exclusive (FullscreenExclusive).
 * Video Mux + countdown + CTA; checkout con Stripe (suscripción mensual USD "Membresía").
 * MercadoPago deshabilitado para membresías — todo país pasa por Stripe.
 *
 * Stripe Price ID: configurado en /admin/suscripciones para `plan_tier=vip`
 * (un solo tier — "Membresía Exclusiva"). El backend lo resuelve automático.
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import { AR_PLANS } from "@/config/arInvitacionPlans";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getDatafastAttributionPayload } from "@/lib/datafast";

const FALLBACK_PLAYBACK_ID = "PhCWZCQmP6hVfAuCokc00jipal401oe2M38PVvBW9NSas";
const PRICE_ARS_STANDARD = 19999;
const PRODUCT_NAME_STANDARD = "Membresía";
// Post-pago: Clerk en `/portal/completar-perfil` → `/pending-approval`.
const SUCCESS_PATH = "/portal/completar-perfil?plan=exclusiva&source=exclusive_ar";

const buildTimePlaybackId =
  (import.meta.env.VITE_EXCLUSIVE_PLAYBACK_ID as string)?.trim() ||
  FALLBACK_PLAYBACK_ID;

// CTA copy fijo (gestión en código; VIP = /exclusive-ar?tier=vip)
const CTA_LABEL_STANDARD = "Descubrir";
const CTA_SUBTITLE_STANDARD = "Ingresar al Canal Privado + Hablar conmigo";
const CTA_LABEL_VIP = "Unirme VIP";
const CTA_SUBTITLE_VIP = "Grupo exclusivo + Hablar conmigo";

function useCountdown(endAtIso: string | null) {
  const [remaining, setRemaining] = useState<{
    h: number;
    m: number;
    s: number;
  } | null>(null);

  useEffect(() => {
    if (!endAtIso || typeof endAtIso !== "string") return;
    const end = new Date(endAtIso).getTime();
    if (Number.isNaN(end)) return;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff <= 0) {
        setRemaining(null);
        return;
      }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setRemaining({ h, m, s });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAtIso]);

  return remaining;
}

async function createStripeSubscription(
  priceArsFallback: number,
  productName: string,
  productId: string,
  buyerEmail: string,
  /** "vip" | "standard" — driver del Price ID en Railway y del nivel de membresía en el webhook Stripe. */
  planTier: "vip" | "standard",
  /** Optional admin-generated invite code. Validated server-side y persistido en la
   * metadata del Stripe Subscription para que el webhook lo correlacione. */
  inviteCode?: string,
): Promise<{ success: true; url: string } | { success: false; error: string }> {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  // Sin priceId — el endpoint resuelve por `plan_tier=vip` desde
  // `membership_products` (admin pega Price ID en /admin/suscripciones).
  // `planTier` queda en metadata para tracking, pero el tier canónico es VIP.
  // Fallback USD aproximado (ARS / 1000 ≈ USD); el monto real lo define el Price ID.
  const fallbackAmountUsdCents = Math.max(100, Math.round((priceArsFallback / 1000) * 100));

  const successUrl = `${base}${SUCCESS_PATH}`;
  const cancelUrl = `${base}/exclusive-ar${planTier === "vip" ? "?tier=vip" : ""}`;

  const response = await fetch(`${base}/api/create-subscription-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
            ...getDatafastAttributionPayload(),
      amount: fallbackAmountUsdCents,
      currency: "usd",
      productName,
      buyerEmail,
      country: "AR",
      successUrl,
      cancelUrl,
      intervalCount: 1,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
      metadata: {
        source: BRAND_SOURCE_KEY,
        site: BRAND_SITE_HOST,
        brand: BRAND_PERSON_NAME,
        type: "membresia_ar",
        flow: "exclusive_ar",
        product_id: productId,
        product_name: productName,
        // Único tier canónico: VIP. `planTier` original queda en metadata
        // para tracking pero el webhook asigna nivel desde `plan_tier`.
        plan_tier: "vip",
        product_type: "exclusive",
        legacy_tier: planTier,
        template: "FullscreenExclusiveAr",
        template_variant: planTier === "vip" ? "exclusive_ar_vip" : "exclusive_ar_standard",
        ...(inviteCode ? { invite_code: inviteCode } : {}),
      },
    }),
  });

  const data = await response.json();
  if (!response.ok || !data.url) {
    return { success: false, error: data.error || "Error al crear checkout" };
  }
  return { success: true, url: data.url };
}

export default function FullscreenExclusiveAr() {
  const [searchParams] = useSearchParams();
  const isVip = searchParams.get("tier") === "vip";
  // Admin-generated invitation (optional). When ?invite=CODE is present we
  // call /api/invitation-codes/validate?probe=1 — `probe=1` does not consume
  // a use, so reloads don't burn through the code's max_uses. If the code is
  // expired/exhausted/inactive we render a wall instead of the checkout.
  const inviteCode = (searchParams.get("invite") || searchParams.get("code") || "").toUpperCase().trim();

  const priceArs = isVip ? AR_PLANS.vip.price : PRICE_ARS_STANDARD;
  const productName = isVip ? AR_PLANS.vip.productName : PRODUCT_NAME_STANDARD;
  const productId = isVip ? "exclusive_ar_membresia_vip" : "exclusive_ar_membresia";

  const [playbackId, setPlaybackId] = useState(buildTimePlaybackId);
  const [countdownEnd, setCountdownEnd] = useState<string | null>(null);
  const [checkoutState, setCheckoutState] = useState<"idle" | "email" | "creating" | "redirecting" | "error">("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const { setTemplateName } = useCurrentTemplate();
  const countdown = useCountdown(countdownEnd);

  // Invite validation state — only relevant when ?invite=CODE is in the URL.
  // For URLs without a code we fall through (open access) to preserve the
  // existing /exclusive-ar behaviour for organic / direct traffic.
  type InviteState =
    | { kind: "no-invite" }
    | { kind: "checking" }
    | { kind: "valid"; expires_at: string | null }
    | { kind: "invalid"; reason: "expired" | "exhausted" | "inactive" | "not_found" | "error" };
  const [inviteState, setInviteState] = useState<InviteState>(
    inviteCode ? { kind: "checking" } : { kind: "no-invite" }
  );

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    fetch(`/api/invitation-codes/validate?code=${encodeURIComponent(inviteCode)}&probe=1`)
      .then((r) => r.json())
      .then((data: { valid?: boolean; reason?: InviteState extends { kind: "invalid"; reason: infer R } ? R : never; valid_until_iso?: string }) => {
        if (cancelled) return;
        if (data.valid) {
          setInviteState({ kind: "valid", expires_at: data.valid_until_iso ?? null });
          // Override the global "exclusive countdown" with this invite's actual
          // expiration so the user sees how long THEIR link lives — not the
          // generic 23h marketing countdown. Burst links (60s, 2min) display
          // as a tight pressure timer instead of a misleading 23h.
          if (data.valid_until_iso) {
            setCountdownEnd(data.valid_until_iso);
          }
        } else {
          const reason = (data.reason ?? "not_found") as "expired" | "exhausted" | "inactive" | "not_found" | "error";
          setInviteState({ kind: "invalid", reason });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Network/server error — fail open so a flaky DB doesn't block paying users.
        setInviteState({ kind: "valid", expires_at: null });
      });
    return () => { cancelled = true; };
  }, [inviteCode]);

  // Auto-expire — when the countdown for an admin invite hits zero, flip the
  // page to the "expired" wall instead of letting the user keep clicking CTAs
  // that the backend will now reject.  Only applies to invite-gated visits
  // (organic /exclusive-ar without ?invite= keeps the global countdown).
  useEffect(() => {
    if (inviteState.kind !== "valid" || !inviteState.expires_at) return;
    const expiresAtMs = new Date(inviteState.expires_at).getTime();
    if (!Number.isFinite(expiresAtMs)) return;
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) {
      setInviteState({ kind: "invalid", reason: "expired" });
      return;
    }
    const timer = window.setTimeout(() => {
      setInviteState({ kind: "invalid", reason: "expired" });
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [inviteState]);

  // Per-mount session id so /admin/live-funnel can group all the events
  // (view → CTA click → email submit → MP redirect) from one visitor.
  const sessionIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `fea_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );

  // Single funnel-event mirror: sends to /api/track-funnel-event so the
  // event lands in funnel_events DB → /admin/live-funnel → Slack #anaafernandez.
  const fireFunnelEvent = useCallback((eventName: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const ph = (window as unknown as { posthog?: { get_distinct_id?: () => string } }).posthog;
    mirrorFunnelEvent({
      event_name: eventName,
      posthog_distinct_id: ph?.get_distinct_id?.(),
      session_id: sessionIdRef.current,
      customer_email: email || undefined,
      template: "FullscreenExclusiveAr",
      template_variant: isVip ? "exclusive_ar_vip" : "exclusive_ar_standard",
      flow_name: "exclusive_ar",
      country: "AR",
      language: "es-AR",
      landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      props: {
        plan_tier: isVip ? "vip" : "standard",
        ...(inviteCode ? { invite_code: inviteCode } : {}),
        ...(props ?? {}),
      },
    });
  }, [email, inviteCode, isVip]);

  // View tracking — fires once per mount.
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current || inviteState.kind === "checking") return;
    viewTrackedRef.current = true;
    // Compute entry_type and time-left so Slack can answer at a glance:
    // "¿llegó con invitación válida? ¿cuánto tiempo le queda al link?"
    const expiresAt =
      inviteState.kind === "valid" ? (inviteState.expires_at ?? null) : null;
    const minutesRemaining = expiresAt
      ? Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 60_000))
      : null;
    const entryType = !inviteCode
      ? "organic"
      : inviteState.kind === "valid"
        ? "invite_valid"
        : inviteState.kind === "invalid"
          ? (inviteState as { kind: "invalid"; reason: string }).reason === "expired"
            ? "invite_expired"
            : "invite_exhausted"
          : "invite_checking";
    fireFunnelEvent("exclusive_ar_view", {
      has_invite: !!inviteCode,
      invite_state: inviteState.kind,
      entry_type: entryType,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
      ...(expiresAt ? { expires_at: expiresAt } : {}),
      ...(minutesRemaining !== null ? { minutes_remaining: minutesRemaining } : {}),
      // Human-readable landing label for #anaafernandez
      landing_label: inviteCode
        ? `invite_${entryType}${minutesRemaining !== null ? ` (${minutesRemaining}min)` : ""}`
        : "organic",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteState.kind]);

  const handleCTAClick = useCallback((_url: string) => {
    if (checkoutState !== "idle" && checkoutState !== "email") return;
    if (checkoutState === "idle") {
      fireFunnelEvent("exclusive_ar_cta_click", {
        funnel_step: "open_email_modal",
      });
      setCheckoutState("email");
      setCheckoutError(null);
      return;
    }
    const trimmed = email.trim();
    if (!trimmed) {
      setCheckoutError("Ingresá tu email para continuar.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setCheckoutError("Ingresá un email válido.");
      return;
    }
    fireFunnelEvent("exclusive_ar_checkout_initiated", {
      funnel_step: "creating_stripe_subscription",
      gateway: "stripe",
      price_ars_fallback: priceArs,
      product_id: productId,
    });
    setCheckoutState("creating");
    setCheckoutError(null);
    createStripeSubscription(
      priceArs,
      productName,
      productId,
      trimmed,
      isVip ? "vip" : "standard",
      inviteCode || undefined,
    )
      .then((result) => {
        if (result.success === true) {
          fireFunnelEvent("exclusive_ar_checkout_created", {
            funnel_step: "redirecting_to_stripe",
            gateway: "stripe",
          });
          setCheckoutState("redirecting");
          window.location.href = result.url;
        } else {
          fireFunnelEvent("exclusive_ar_checkout_error", {
            error: result.error,
            funnel_step: "stripe_subscription_failed",
          });
          setCheckoutState("error");
          setCheckoutError(result.error);
        }
      })
      .catch((e) => {
        fireFunnelEvent("exclusive_ar_checkout_error", {
          error: e instanceof Error ? e.message : "Network error",
          funnel_step: "fetch_exception",
        });
        setCheckoutState("error");
        setCheckoutError("Error de conexión. Intenta de nuevo.");
      });
  }, [checkoutState, email, priceArs, productName, productId, isVip, inviteCode, fireFunnelEvent]);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, []);

  useEffect(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (!base) return;
    fetch(`${base}/api/public-playback-ids`)
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: { exclusive?: string; exclusiveCountdownEnd?: string } | null,
        ) => {
          const id = data?.exclusive?.trim();
          if (id) setPlaybackId(id);
          const end = data?.exclusiveCountdownEnd?.trim();
          if (end) setCountdownEnd(end);
        },
      )
      .catch(() => {});
  }, []);

  // ── Invitation-expired wall ─────────────────────────────────────────────
  // When an admin-generated invitation link is invalid, block the checkout
  // and explain why. Organic traffic (no ?invite= param) skips this entirely.
  if (inviteState.kind === "invalid") {
    const headline =
      inviteState.reason === "expired"
        ? "Esta invitación ya expiró"
        : inviteState.reason === "exhausted"
          ? "Esta invitación alcanzó su límite de usos"
          : inviteState.reason === "inactive"
            ? "Esta invitación fue desactivada"
            : "Invitación no válida";
    return (
      <div className="w-full h-dvh max-h-dvh flex items-center justify-center bg-black px-6 text-center">
        <div className="max-w-md space-y-4">
          <div className="text-5xl">⏳</div>
          <h1 className="text-2xl font-semibold text-white">{headline}</h1>
          <p className="text-zinc-400 text-sm">
            Si recibiste este link de un admin, pediles uno nuevo. Si pensás que es un error,
            contactanos por DM.
          </p>
          <a
            href="/"
            className="inline-block rounded-full bg-white px-6 py-2 text-sm font-medium text-black hover:bg-zinc-200"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  // Loading state while we check the code (only if there IS one)
  if (inviteState.kind === "checking") {
    return (
      <div className="w-full h-dvh max-h-dvh flex items-center justify-center bg-black">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      </div>
    );
  }

  return (
    <div className="w-full h-dvh max-h-dvh overflow-hidden bg-black relative">
      {/* Overlay: formulario de email (primer click en CTA) */}
      {checkoutState === "email" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm space-y-4">
            <p className="text-white text-center text-lg">Ingresá tu email para continuar</p>
            <input
              type="email"
              placeholder="Tu email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setCheckoutError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleCTAClick("/exclusive-ar")}
              className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 focus:border-white/40 focus:outline-none"
            />
            {checkoutError && <p className="text-center text-sm text-red-300">{checkoutError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setCheckoutState("idle");
                  setEmail("");
                  setCheckoutError(null);
                }}
                className="flex-1 rounded-lg bg-white/20 px-4 py-2 text-white text-sm hover:bg-white/30"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleCTAClick("/exclusive-ar")}
                className="flex-1 rounded-lg bg-white px-4 py-2 text-black text-sm font-medium hover:bg-white/90"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Overlay de estado del checkout (creando / redirigiendo / error) */}
      {checkoutState === "creating" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <p className="text-white text-lg">Preparando checkout...</p>
        </div>
      )}
      {checkoutState === "redirecting" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <p className="text-white text-lg">Redirigiendo a Mercado Pago...</p>
        </div>
      )}
      {checkoutState === "error" && checkoutError && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="text-center max-w-sm">
            <p className="text-white/90 mb-4">{checkoutError}</p>
            <button
              type="button"
              onClick={() => {
                setCheckoutState("idle");
                setCheckoutError(null);
              }}
              className="rounded-lg bg-white/20 px-4 py-2 text-white text-sm hover:bg-white/30"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Countdown — compact format for short links, full hh:mm:ss for the
          global 23h timer. When ?invite= is present and the link is short
          (<5 min) we drop the hours to keep it readable: "00:42" instead of
          "00:00:42". Color also shifts to amber/red as time runs out. */}
      {countdown && (() => {
        const totalSeconds = countdown.h * 3600 + countdown.m * 60 + countdown.s;
        const isShort = totalSeconds < 60 * 5; // under 5 min — burst link mode
        const display = isShort
          ? `${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`
          : `${String(countdown.h).padStart(2, "0")}:${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`;
        const urgency =
          totalSeconds <= 10 ? "text-red-400 border-red-500/60 animate-pulse"
          : totalSeconds <= 30 ? "text-amber-300 border-amber-400/50"
          : totalSeconds <= 60 ? "text-amber-200 border-amber-300/30"
          : "text-white border-white/20";
        const isInviteCountdown = inviteState.kind === "valid" && !!inviteState.expires_at;
        return (
          <div
            className={`absolute top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-xl bg-black/70 text-center backdrop-blur-sm border ${urgency}`}
            aria-live="polite"
            aria-label={`Tiempo restante ${display}`}
          >
            <span className="text-xs uppercase tracking-wider text-white/80 block mb-0.5">
              {isInviteCountdown ? "Tu invitación expira en" : "Termina en"}
            </span>
            <span className="font-body text-lg font-bold tabular-nums tracking-tight">
              {display}
            </span>
          </div>
        );
      })()}

      <MuxVideoPlayerWithCTA
        playbackId={playbackId}
        variant="fullscreen"
        ctaLabel={isVip ? CTA_LABEL_VIP : CTA_LABEL_STANDARD}
        ctaSubtitle={isVip ? CTA_SUBTITLE_VIP : CTA_SUBTITLE_STANDARD}
        hideUnmutePrompt
        ctaUrl="/exclusive-ar"
        onCTAClick={handleCTAClick}
      />
    </div>
  );
}
